import React, { useEffect, useRef, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import styles from '../Dashboards.module.css';
import Spinner from '../../common/Spinner';
import { useAhaPanelContents, useHtmlSnapshot } from '../hooks';
import CustomPivotChart from 'javascripts/bookmarks/legacy_custom_chart';
import AhaContents from './AhaContents';
import { debounce } from 'lodash';
import NivoChart from './NivoChart';
import customElementBoolAttr from 'javascripts/util/customElementBoolAttr';

const GoogleChart = ({
  panel,
  preview,
  fullScreen,
  width,
  height,
  padding,
  bookmarkId,
  filters,
}) => {
  const shouldUseHtmlSnapshot = useHtmlSnapshot();

  const [paddingVertical, paddingHorizontal] = padding;

  const ahaContents = useAhaPanelContents({
    panel,
    bookmarkId,
    preview,
    showLegend: panel.showLegend,
    dashboardFilter: panel.dashboardFilter,
    filters,
  });

  const panelContentRef = useRef(null);
  const [isNivo, setIsNivo] = useState(false);

  const getLegend = chartJSON => {
    let legend = chartJSON.renderOptions?.options?.legend || 'none';
    if (panel.showLegend) {
      if (legend === 'none') {
        legend = true;
      }
    } else {
      legend = 'none';
    }

    return legend;
  };

  const getChartArea = chartJSON => {
    const chartType = chartJSON.renderOptions?.chartType;
    const legend = chartJSON.renderOptions.options.legend;

    const chartArea = {
      bottom: '10%',
      left: '10%',
      height: '85%',
      width: '85%',
    };

    // Different chart area to account for axis' labels and the position of the legend
    switch (chartType) {
      case 'PieChart':
        if (legend === 'none') {
          chartArea.height = '100%';
          chartArea.width = '100%';
        }
        break;
      case 'ColumnChart':
      case 'BarChart':
      case 'LineChart':
      case 'ScatterChart':
        switch (legend) {
          case 'none':
            chartArea.height = '100%';
            chartArea.width = '100%';
            break;
          case 'top':
            chartArea.height = '80%';
            break;
          case 'right':
          case true: // defaults to 'right'
            chartArea.width = '70%';
            break;
          case 'bottom':
            chartArea.height = '75%';
            chartArea.bottom = '20%';
            break;
          default:
            // defaults to 'right'
            chartArea.width = '70%';
            break;
        }
        break;
    }

    return chartArea;
  };

  const renderChart = useCallback(() => {
    if ((ahaContents || shouldUseHtmlSnapshot) && panelContentRef.current) {
      requestAnimationFrame(() => {
        try {
          const chartDiv = document.getElementById(
            `custom-pivot-chart-${panel.bookmarkId}${preview ? '-preview' : ''}`
          );

          if (chartDiv.getAttribute('data-nivo-chart') === 'true') {
            setIsNivo(true);
            return;
          }

          const chartJSON = JSON.parse(chartDiv.getAttribute('data-json'));

          const bounding = panelContentRef.current.getBoundingClientRect();
          chartJSON.forceWidth = bounding.width - paddingHorizontal * 2;
          chartJSON.forceHeight = bounding.height - paddingVertical * 2;

          if (!chartJSON.renderOptions.options)
            chartJSON.renderOptions.options = {};

          chartJSON.renderOptions.options.legend = getLegend(chartJSON);
          const chartArea = getChartArea(chartJSON);

          if (fullScreen) {
            chartJSON.globalOverrides = {
              title: null,
              legend: true,
            };
          } else {
            chartJSON.globalOverrides = {
              title: null,
              chartArea: chartArea,
            };
          }
          // eslint-disable-next-line no-unused-vars
          const _chart = new CustomPivotChart(
            `custom-pivot-chart-${panel.bookmarkId}${
              preview ? '-preview' : ''
            }`,
            chartJSON
          );
        } catch (error) {
          console.log(error);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ahaContents,
    panel.bookmarkId,
    preview,
    paddingHorizontal,
    paddingVertical,
    fullScreen,
    width,
    height,
    shouldUseHtmlSnapshot,
    panel.showLegend,
  ]);
  // Need width/height above in order for the chart to immediately re-render on panel resize

  useEffect(renderChart, [renderChart]);

  useEffect(() => {
    const handleWindowResize = debounce(() => {
      renderChart();
    }, 150);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  });

  let panelContent;
  if (ahaContents || shouldUseHtmlSnapshot) {
    panelContent = <AhaContents ahaContents={ahaContents} />;
  } else {
    panelContent = <Spinner />;
  }

  if (isNivo) {
    return <NivoChart panel={panel} width={width} height={height} />;
  } else {
    const isLoading = !ahaContents && !shouldUseHtmlSnapshot;

    return (
      <div
        className={styles.PanelContent}
        aria-label='Graph Panel'
        ref={panelContentRef}
        data-async-loading={customElementBoolAttr(isLoading)}
      >
        {panelContent}
      </div>
    );
  }
};

const mapStateToProps = state => ({
  bookmarkId: state.getIn(['meta', 'bookmarkId']),
  filters: state.getIn(['meta', 'filters']),
});

export default connect(mapStateToProps)(GoogleChart);
